/**
 * Why this trip was reported as a no-show — on the dispatcher's trip screen,
 * where the office first hears about it.
 *
 * A no-show is a claim against the customer, and the first thing anyone has
 * to know is WHICH CLOCK let it through: 60 minutes counted from a real
 * touchdown, or no landing time at all and somebody in the office agreed to
 * it. That line is the card's headline for exactly that reason.
 *
 * It states what is NOT proven as plainly as what is. An unticked step is
 * shown unticked, and a report the phone gave no fix for says so — roughly
 * 7% of driver events carry none, and an omitted line reads as agreement to
 * anyone scanning.
 *
 * NO PHOTOS HERE. They are in the private receipts bucket and the dispatcher
 * stack has no access to it (it is not given the bucket name, by design), so
 * this says how many were filed and the dashboard's Driver events tab is
 * where they are opened. The vocabulary is shared with that tab
 * (Utils/noShowEvidence) so the two screens cannot describe one claim in two
 * ways.
 */
import React from 'react';
import { formatDateTime } from '../../../Utils/timezone';
import {
  NO_SHOW_POSITION_MISSING,
  noShowCallText,
  noShowCheckpointRows,
  noShowNameSignText,
  noShowPoint,
  noShowWaitText,
} from '../../../Utils/noShowEvidence';
import { nameSignAnswer, noShowEvidence } from '../portalFormat';

export default function NoShowEvidenceCard({ trip }) {
  const evidence = noShowEvidence(trip);
  if (!evidence) return null;

  const point = noShowPoint(evidence);
  const photos = Number(evidence.photo_count) || 0;
  // From the booking's own record, not a copy taken at report time: one
  // fact, one home, and it cannot disagree with the event log.
  const sign = noShowNameSignText(nameSignAnswer(trip));

  return (
    <div className="pp-card pp-op-noshow" data-testid="dispatcher-no-show-evidence">
      <div className="pp-eyebrow">No-show reported</div>
      <div className="pp-op-noshow__basis">{noShowWaitText(evidence)}</div>
      <div className="pp-op-noshow__when">
        {formatDateTime(new Date(evidence.reported_at))}
        {evidence.landed_at ? ` · landed ${formatDateTime(new Date(evidence.landed_at))}` : ''}
      </div>

      <ul className="pp-op-noshow__steps">
        {noShowCheckpointRows(evidence).map((row) => (
          <li key={row.key} className={row.confirmed ? 'is-yes' : 'is-no'}>{row.label}</li>
        ))}
      </ul>

      <div className="pp-op-noshow__facts">
        <span>{noShowCallText(evidence)}</span>
        {sign ? <span>{sign}</span> : null}
        <span className={point ? '' : 'is-no'}>
          {point ? `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}` : NO_SHOW_POSITION_MISSING}
        </span>
        {photos ? <span>{photos === 1 ? '1 photo filed' : `${photos} photos filed`}</span> : null}
        {evidence.office_agreed ? <span>Agreed with the office beforehand</span> : null}
      </div>

      {evidence.note ? <p className="pp-op-noshow__note">{evidence.note}</p> : null}
    </div>
  );
}
